import axios from "axios";
import dayjs, { Dayjs } from "dayjs";

import { CalendarEvent } from "@/api/calendarTypes";

interface GraphCalendarResponse {
  value: CalendarEvent[];
}

// Cache for resource calendar events
const resourceEventsCache: Record<string, CalendarEvent[]> = {};
const cacheExpiry: Record<string, number> = {};
const CACHE_TTL_MS = 30 * 1000; // 30 seconds

export async function getResourceCalendarEvents(
  token: string,
  resourceEmail: string, // E-post til møterommet, f.eks. "møterom1@domain.com"
  start: string | Date | Dayjs, // ISO 8601 string, Date object, or dayjs object
  end: string | Date | Dayjs, // ISO 8601 string, Date object, or dayjs object
  skipCache = false // Set to true to bypass cache
): Promise<CalendarEvent[]> {
  // Convert start and end to ISO 8601 strings
  const startStr =
    typeof start === "string"
      ? start
      : start instanceof Date
      ? start.toISOString()
      : start.toISOString();
  const endStr =
    typeof end === "string"
      ? end
      : end instanceof Date
      ? end.toISOString()
      : end.toISOString();

  // Create cache key
  const cacheKey = `${resourceEmail}_${startStr}_${endStr}`;

  // Check cache first and verify it hasn't expired
  const now = Date.now();
  if (
    !skipCache &&
    resourceEventsCache[cacheKey] &&
    cacheExpiry[cacheKey] &&
    cacheExpiry[cacheKey] > now
  ) {
    return resourceEventsCache[cacheKey];
  }

  // Use users endpoint - works for both users and room mailboxes
  const url = `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(
    resourceEmail
  )}/calendar/calendarView?startDateTime=${encodeURIComponent(
    startStr
  )}&endDateTime=${encodeURIComponent(endStr)}`;

  let response: { data: GraphCalendarResponse };
  try {
    response = await axios.get<GraphCalendarResponse>(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Prefer: 'outlook.timezone="Europe/Oslo"',
      },
    });
  } catch (err) {
    // Clear any stale cache for this key so an unauthorized user doesn't
    // keep seeing cached data from a previous authorized session.
    delete resourceEventsCache[cacheKey];
    delete cacheExpiry[cacheKey];
    throw err;
  }

  const events = response.data.value;

  // Store in cache with expiry
  resourceEventsCache[cacheKey] = events;
  cacheExpiry[cacheKey] = Date.now() + CACHE_TTL_MS;

  return events;
}
